import React, { useState, useEffect, useRef } from "react"
import { useParams, useNavigate, Link } from "react-router-dom"
import {
  getContentById,
  updateContentApi,
  uploadMedia,
  getContentOptions,
} from "api/categories"
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Button,
  Form,
  FormGroup,
  Label,
  Input,
  FormFeedback,
} from "reactstrap"
import Breadcrumbs from "components/Common/Breadcrumb"
import * as Yup from "yup"
import { useFormik } from "formik"

const EditEducationalContent = () => {
  document.title = "Edit Educational Content | CA Portal"

  const { id } = useParams()
  const navigate = useNavigate()
  const [formData, setFormData] = useState({
    contentCategoryId: 5, // Pre-specify contentCategoryId here
  })
  const [file, setFile] = useState(null)
  const [imageUrl, setImageUrl] = useState("")
  const descriptionRef = useRef(null)
  const fileInputRef = useRef(null)

  useEffect(() => {
    const fetchContentDetails = async () => {
      try {
        const response = await getContentById(id)
        setFormData(response.data)
        setImageUrl(response.data.imageUrl)
      } catch (error) {
        console.error("Error fetching content details:", error)
      }
    }

    const fetchContentOptions = async () => {
      try {
        const response = await getContentOptions(0, 10)
        const filteredData = response.data.filter(item => item.id === 5)
        // setContentOptions(filteredData) // Uncomment this if you need content options
      } catch (error) {
        console.error("Error fetching content options:", error)
      }
    }

    fetchContentDetails()
    fetchContentOptions()
  }, [id])

  const validationSchema = Yup.object({
    title: Yup.string().required("Please Enter Content Title"),
  })

  const formik = useFormik({
    initialValues: {
      title: formData.title || "",
      description: formData.description || "",
    },
    enableReinitialize: true,
    validationSchema: validationSchema,
    onSubmit: async (values, { setSubmitting }) => {
      try {
        let updatedImageUrl = formData.imageUrl || imageUrl

        if (file) {
          const mediaResponse = await uploadMedia(file)
          updatedImageUrl = mediaResponse.data
        }

        const contentData = {
          ...formData,
          title: values.title,
          description: values.description,
          imageUrl: updatedImageUrl,
        }

        await updateContentApi(contentData)

        alert("Content updated successfully")
        navigate("/learningcentre")
      } catch (error) {
        console.error("Error updating content:", error)
        alert("Failed to update content. Please try again.")
      } finally {
        setSubmitting(false)
      }
    },
  })

  const handleFileChange = e => {
    const newFile = e.target.files[0]
    if (newFile) {
      // New file selected
      setFile(newFile)
      const fileUrl = URL.createObjectURL(newFile)
      setImageUrl(fileUrl)
    } else {
      // No new file selected, keep existing media
      setImageUrl(formData.imageUrl)
    }
  }

  const handleRemoveFile = () => {
    setFile(null)
    setImageUrl("")
    if (fileInputRef.current) {
      fileInputRef.current.value = ""
    }
    formik.setFieldValue("mediafile", "")
  }

  const getFileType = (url) => {
    if (!url) return ""
    
    const extension = url.split(".").pop().toLowerCase()
    if (extension === "mp4") {
      return "video"
    } else if (["pptx", "docx", "pdf"].includes(extension)) {
      return "document"
    } else {
      return "image"
    }
  }

  const handleCancel = () => {
    navigate("/learningcentre")
  }

  return (
    <div className="page-content">
      <Container fluid>
        <div className="d-flex justify-content-between mb-3">
          <Breadcrumbs
            title="Content"
            breadcrumbItem="Edit Educational Content"
          />
          <Link to="/learningcentre">
            <Button type="button" color="primary">
              <i className="bx bx-arrow-back"></i> Back
            </Button>
          </Link>
        </div>
        <Row>
          <Col xl="12">
            <Card>
              <CardBody>
                <Col>
                  <Card style={{ borderRadius: "10px", marginTop: "10px" }}>
                    <CardBody className="p-4">
                      <Form
                        className="form-horizontal"
                        onSubmit={formik.handleSubmit}
                      >
                        <FormGroup>
                          <Label for="title">Title</Label>
                          <Input
                            type="text"
                            name="title"
                            id="title"
                            className="form-control"
                            value={formik.values.title}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            invalid={
                              formik.touched.title && !!formik.errors.title
                            }
                          />
                          {formik.touched.title && formik.errors.title && (
                            <FormFeedback>{formik.errors.title}</FormFeedback>
                          )}
                        </FormGroup>
                      
                        <FormGroup>
                          <div className="mb-3">
                            <label className="form-label">
                              Click to Upload Media Here (Optional)
                            </label>
                            <div className="d-flex align-items-center">
                              <input
                                type="file"
                                className="form-control flex-grow-1 me-3"
                                name="mediafile"
                                placeholder="Click to Upload Media Here"
                                id="mediafile"
                                onChange={handleFileChange}
                                ref={fileInputRef}
                              />
                              {imageUrl && (
                                <>
                                  {getFileType(imageUrl) === "image" && (
                                    <img
                                      src={imageUrl}
                                      alt="Uploaded"
                                      style={{ maxWidth: "100px" }}
                                    />
                                  )}
                                  {getFileType(imageUrl) === "video" && (
                                    <video
                                      controls
                                      autoPlay
                                      style={{ maxWidth: "100px" }}
                                    >
                                      <source src={imageUrl} type="video/mp4" />
                                      Your browser does not support the video
                                      tag.
                                    </video>
                                  )}
                                  {getFileType(imageUrl) === "document" && (
                                    <a
                                      href={imageUrl}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                    >
                                      View Document
                                    </a>
                                  )}
                                  <Button
                                    color="danger"
                                    onClick={handleRemoveFile}
                                    className="ms-3"
                                  >
                                    Remove
                                  </Button>
                                </>
                              )}
                            </div>
                          </div>
                        </FormGroup>

                        <FormGroup>
                          <Label for="description">Description:</Label>
                          <Input
                            type="textarea"
                            id="description"
                            name="description"
                            innerRef={descriptionRef}
                            value={formik.values.description}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            invalid={
                              formik.touched.description &&
                              !!formik.errors.description
                            }
                            style={{ overflow: "hidden", resize: "none" }}
                          />
                          {formik.touched.description &&
                            formik.errors.description && (
                              <FormFeedback>
                                {formik.errors.description}
                              </FormFeedback>
                            )}
                        </FormGroup>
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "center",
                            marginTop: "20px",
                          }}
                        >
                          <Button
                            color="success"
                            style={{ marginRight: "10px" }}
                            size="lg"
                            type="submit"
                            disabled={formik.isSubmitting}
                          >
                            Save
                          </Button>
                          <Button
                            color="primary"
                            size="lg"
                            onClick={handleCancel}
                          >
                            Cancel
                          </Button>
                        </div>
                      </Form>
                    </CardBody>
                  </Card>
                </Col>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  )
}

export default EditEducationalContent
